import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import SEO from '@/components/SEO';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-3">{title}</h2>
      <div className="text-gray-700 dark:text-zinc-300 space-y-3 leading-relaxed">{children}</div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950">
      <SEO
        title="About Open Card Vault"
        description="Learn about Open Card Vault — an independent marketplace and community platform built for trading-card collectors of all experience levels."
        url="/about"
      />
      <div className="bg-gradient-to-r from-brand-600 to-accent-600 text-white py-12">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-2">
            <Info className="h-7 w-7" />
            <h1 className="text-3xl font-display font-bold">About Open Card Vault</h1>
          </div>
          <p className="text-brand-100">Who we are and why we built this</p>
        </div>
      </div>

      <div className="container-custom py-10 max-w-3xl">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-8">

          <p className="text-gray-700 dark:text-zinc-300 mb-8 leading-relaxed">
            Open Card Vault is an independent marketplace and community platform built for
            trading-card collectors. Whether you are just opening your first pack or have been
            collecting for decades, Open Card Vault gives you the tools to manage your collection,
            connect with other collectors, buy, sell, and trade cards, and learn about the hobby.
          </p>

          <Section title="What Is Open Card Vault?">
            <p>
              Open Card Vault is a platform where collectors can buy, sell, and trade physical
              trading cards and collectibles across multiple games and sports. The platform
              combines a marketplace with community features — including a discussion forum,
              wishlists, collection tracking, a deck builder, and a progression system — in
              one place designed specifically for the card-collecting hobby.
            </p>
            <p>
              The platform is built around the idea that collecting should be accessible,
              trustworthy, and community-driven. Every feature — from condition grading guides
              to the card mention system in the forum — exists to make the hobby easier to
              participate in and more enjoyable for everyone involved.
            </p>
          </Section>

          <Section title="Who Is It For?">
            <p>Open Card Vault is open to collectors aged 13 and older.</p>
            <p>
              Younger collectors can use the platform to build and track their collection,
              maintain a wishlist, participate in the forum, build decks, and earn achievements —
              without access to financial transactions.
            </p>
            <p>
              Collectors aged 18 and older have access to the full marketplace, including buying,
              selling, running auctions, making offers, and conducting card-for-card trades.
            </p>
            <p>
              Whether you collect Pokémon, Yu-Gi-Oh!, Magic: The Gathering, sports cards,
              or any other trading-card game, Open Card Vault is built to support your hobby.
            </p>
          </Section>

          <Section title="Why We Built It">
            <p>
              The trading-card hobby has grown significantly, but finding a platform that
              genuinely serves collectors — rather than just processing transactions — has
              remained a challenge. Most platforms treat cards like any other product. Open
              Card Vault is built specifically for the way collectors think about cards: by
              condition, by set, by rarity, by game, and by community.
            </p>
            <p>
              We built Open Card Vault to give collectors a single place to track everything
              they own, find the cards they want, connect with people who share the same
              hobby, and conduct transactions they can trust. The community forum, wishlists,
              collection management, and marketplace are all designed to work together as a
              complete collecting experience — not just a listing site with a chat box bolted on.
            </p>
            <p>
              We also believe the hobby is better when newer collectors have access to good
              information. The Beginner's Guide section of our forum exists specifically to
              help people learn about condition grading, storage, pricing, spotting counterfeits,
              and other fundamentals that experienced collectors take for granted.
            </p>
          </Section>

          <Section title="Community and Safety">
            <p>
              Open Card Vault is a moderated community. We take the safety of our users
              seriously, particularly younger collectors. The platform has a zero-tolerance
              policy for exploitation, harassment, fraud, and counterfeit listings.
            </p>
            <p>
              All users agree to the{' '}
              <Link to="/legal/community-guidelines" className="text-brand-600 dark:text-brand-400 hover:underline">
                Community Guidelines
              </Link>{' '}
              and{' '}
              <Link to="/legal/terms" className="text-brand-600 dark:text-brand-400 hover:underline">
                Terms of Service
              </Link>{' '}
              when creating an account. Reports of violations are reviewed by our moderation
              team, and serious issues are escalated as required by law.
            </p>
          </Section>

          <Section title="Independent Platform">
            <p>
              Open Card Vault is an independently operated platform. It is not affiliated with,
              endorsed by, or sponsored by The Pokémon Company, Nintendo, Konami, Wizards of
              the Coast, Hasbro, Upper Deck, Panini, Topps, or any other trading-card publisher,
              game manufacturer, or sports league.
            </p>
            <p>
              All card names, game titles, set names, and brand references appearing on this
              platform are the property of their respective owners and are used solely for
              identification and descriptive purposes in the context of a secondary marketplace
              for collectible trading cards.
            </p>
          </Section>

          <Section title="Contact Us">
            <p>
              We are a small, independently operated team. If you have questions, feedback,
              or need support, we want to hear from you.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                General support:{' '}
                <a href="mailto:support@opencardvault.com" className="text-brand-600 dark:text-brand-400 hover:underline">
                  support@opencardvault.com
                </a>
              </li>
              <li>
                Privacy inquiries:{' '}
                <a href="mailto:privacy@opencardvault.com" className="text-brand-600 dark:text-brand-400 hover:underline">
                  privacy@opencardvault.com
                </a>
              </li>
              <li>
                Legal matters:{' '}
                <a href="mailto:legal@opencardvault.com" className="text-brand-600 dark:text-brand-400 hover:underline">
                  legal@opencardvault.com
                </a>
              </li>
            </ul>
            <p>
              You can also visit our <Link to="/contact" className="text-brand-600 dark:text-brand-400 hover:underline">Contact page</Link> for
              more details.
            </p>
          </Section>

          <div className="border-t border-gray-200 dark:border-zinc-800 pt-6 mt-8 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-zinc-500">
            <Link to="/guide" className="hover:text-brand-600 dark:hover:text-brand-400">How It Works</Link>
            <Link to="/legal/privacy" className="hover:text-brand-600 dark:hover:text-brand-400">Privacy Policy</Link>
            <Link to="/legal/terms" className="hover:text-brand-600 dark:hover:text-brand-400">Terms of Service</Link>
            <Link to="/legal/community-guidelines" className="hover:text-brand-600 dark:hover:text-brand-400">Community Guidelines</Link>
            <Link to="/contact" className="hover:text-brand-600 dark:hover:text-brand-400">Contact Us</Link>
          </div>

        </div>
      </div>
    </div>
  );
}
